import { useMemo, useRef } from 'react';
import type { Permission } from '@technohull/shared';
import { useAuth } from '../auth/AuthContext.js';
import { EmptyState, PageHeader } from '../components/ui.js';
import { HubGraph, HubTools, useHubView, type HubControls } from '../components/HubGraph.js';

// ── the assembly area as an interactive 3D map (same presentation as Explore) ────────────────────
// A full-bleed "spider web" of the pages the assembly floor works in. Nodes and cross-links are
// filtered to what the signed-in user can actually reach.
const MAP: Array<{ label: string; to: string; perm: Permission; group: string }> = [
  { label: 'Assembly Progress', to: '/assembly', perm: 'production:read', group: 'Build' },
  { label: 'Assembly Board', to: '/board', perm: 'board:read', group: 'Build' },
  { label: 'Assembly Weights', to: '/assembly-weights', perm: 'production:read', group: 'Build' },

  { label: 'GRP Inspection', to: '/grp', perm: 'grp:read', group: 'Checks' },
  { label: 'PDI', to: '/pdi', perm: 'pdi:read', group: 'Checks' },
  { label: 'DS - Programming', to: '/ds', perm: 'ds:read', group: 'Checks' },
  { label: 'Deck Flooring', to: '/deck', perm: 'overview:read', group: 'Checks' },
  { label: 'Loading', to: '/loading', perm: 'overview:read', group: 'Checks' },

  { label: 'Schedule', to: '/schedule', perm: 'schedule:read', group: 'Plan' },
  { label: 'Overview', to: '/overview', perm: 'overview:read', group: 'Plan' },
  { label: 'Pre-Delivery Alerts', to: '/assembly-alerts', perm: 'overview:read', group: 'Plan' },
  { label: 'Assembly Reporting', to: '/reporting', perm: 'report:read', group: 'Plan' },

  { label: 'Labels', to: '/labels', perm: 'labels:read', group: 'Output' },
  { label: 'Info / Files', to: '/info-files', perm: 'production:read', group: 'Output' },
];
// semantic cross-links — the strands that tie the clusters into one web.
const CROSS: Array<[string, string]> = [
  ['Assembly Progress', 'Assembly Board'],
  ['Assembly Board', 'Overview'],
  ['Overview', 'Schedule'],
  ['Schedule', 'Pre-Delivery Alerts'],
  ['Overview', 'Pre-Delivery Alerts'],
  ['Assembly Board', 'Assembly Reporting'],
  ['Assembly Progress', 'PDI'],
  ['GRP Inspection', 'PDI'],
  ['PDI', 'DS - Programming'],
  ['Deck Flooring', 'Loading'],
  ['Labels', 'Overview'],
];

export function AssemblyHub() {
  const { can, canSeePath } = useAuth();
  const controls = useRef<HubControls | null>(null);
  const [view, setView] = useHubView();
  const { items, links } = useMemo(() => {
    const nodes = MAP.filter((l) => can(l.perm) && canSeePath(l.to));
    const present = new Set(nodes.map((n) => n.label));
    return {
      items: nodes.map((n) => ({ label: n.label, path: n.to, group: n.group })),
      links: CROSS.filter(([a, b]) => present.has(a) && present.has(b)),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [can, canSeePath]);

  if (items.length === 0) {
    return (
      <>
        <PageHeader title="Assembly" />
        <EmptyState title="No access" hint="You don't have permission to view any assembly pages." />
      </>
    );
  }

  // Full-bleed, box-less map with the same quiet floating glass controls as Explore.
  return (
    <HubGraph fullBleed center="Assembly" items={items} links={links} controlsRef={controls} view={view}>
      <div className="explore-ui">
        <h1 className="explore-greeting ex-glass">Assembly</h1>
        <HubTools controls={controls} view={view} onView={setView} />
      </div>
    </HubGraph>
  );
}
